import { c as _c } from "react/compiler-runtime";
import type { ConditionedBlock, DateSlotSelectionBlock, FormFieldBlock, FormSection, HofSelectionBlock, JobSelectionBlock } from '@/features/payload-cms/components/form/types';
import { getFormStorageKey } from '@/features/payload-cms/components/form/utils/get-form-storage-key';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FieldName, UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
export interface UseFormStepsReturn {
  currentStepIndex: number;
  setCurrentStepIndex: (index: number | ((previous: number) => number)) => void;
  currentActualStep: FormSection | undefined; // Add undefined possibility
  steps: FormSection[];
  isFirstStep: boolean;
  isLastStep: boolean;
  next: (event?: React.MouseEvent) => Promise<boolean>;
  prev: (event?: React.MouseEvent) => void;
}

/** Height of the sticky navigation covering the top of the viewport. */
const STICKY_NAV_OFFSET = 100;

/**
 * Brings the top of the form back into view after a step change, and only then.
 *
 * Moving the page while the helper can already see where the step starts is jarring, and a
 * cut-off bottom does not call for it either: the next step reads from the top. Measuring
 * before the new step renders is fine, since a step change only moves the bottom edge.
 */
const scrollFormTopIntoView = (formId?: string): void => {
  if (typeof formId !== 'string' || formId === '') return;
  const element = document.querySelector(`#${CSS.escape(formId)}`);
  if (element === null) return;
  const {
    top
  } = element.getBoundingClientRect();
  const isTopVisible = top >= STICKY_NAV_OFFSET && top < window.innerHeight;
  if (isTopVisible) return;
  window.scrollTo({
    top: top + window.scrollY - STICKY_NAV_OFFSET,
    behavior: 'smooth'
  });
};
type SectionField = FormFieldBlock | ConditionedBlock | JobSelectionBlock | HofSelectionBlock | DateSlotSelectionBlock;

/** Names of the fields a `dateSlotSelection` block registers — it owns up to two. */
const getFieldNames = (field: FormFieldBlock | JobSelectionBlock | HofSelectionBlock | DateSlotSelectionBlock): string[] => {
  const names: string[] = [];
  if ('name' in field && typeof field.name === 'string' && field.name !== '') {
    names.push(field.name);
  }
  if (field.blockType === 'dateSlotSelection' && typeof field.ressortName === 'string' && field.ressortName !== '') {
    names.push(field.ressortName);
  }
  // the Besuchstag field also asks whom they visit, the persons, lunch and, without a Hof, the day
  if (field.blockType === 'visitDaySelection') {
    for (const name of [field.visitorTypeField, field.childNameField, field.lunchField]) {
      if (typeof name === 'string' && name !== '') names.push(name);
    }
    if (typeof field.maxPersons === 'number') names.push(field.personCountField);
    names.push(field.dayField);
  }
  return names;
};

/** Every field name under a list of blocks, conditioned ones included. */
const collectFieldNames = (fields: SectionField[]): string[] => fields.flatMap(field => field.blockType === 'conditionedBlock' ? collectFieldNames(field.fields) : getFieldNames(field));
export const useFormSteps = (sections, formMethods, formId, t0) => {
  const $ = _c(52);
  const persistStep = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== sections) {
    t2 = sections.map(_temp);
    $[0] = sections;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  t1 = t2;
  const conditionFieldNames = t1;
  let t3;
  if ($[2] !== conditionFieldNames || $[3] !== formMethods.control) {
    t3 = {
      control: formMethods.control,
      name: conditionFieldNames
    };
    $[2] = conditionFieldNames;
    $[3] = formMethods.control;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const conditionValues = useWatch(t3) as (string | number | boolean | undefined)[];
  let t4;
  if ($[5] !== conditionFieldNames || $[6] !== conditionValues || $[7] !== sections) {
    let t5;
    if ($[9] !== conditionFieldNames || $[10] !== conditionValues) {
      t5 = (section_0, index) => {
        if (conditionFieldNames[index] === "") {
          return "1";
        }
        const expected = section_0.displayCondition?.value ?? "";
        return String(conditionValues[index] ?? "") === expected ? "1" : "0";
      };
      $[9] = conditionFieldNames;
      $[10] = conditionValues;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t4 = sections.map(t5).join("");
    $[5] = conditionFieldNames;
    $[6] = conditionValues;
    $[7] = sections;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const visibilityKey = t4;
  let t5;
  let t6;
  if ($[12] !== sections || $[13] !== visibilityKey) {
    let t7;
    if ($[15] !== visibilityKey) {
      t7 = (_, index_0) => visibilityKey[index_0] === "1";
      $[15] = visibilityKey;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t6 = sections.filter(t7);
    $[12] = sections;
    $[13] = visibilityKey;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  t5 = t6;
  const steps = t5;
  const previousVisibilityKey = useRef(undefined);
  const {
    unregister
  } = formMethods;
  let t7;
  let t8;
  if ($[17] !== conditionFieldNames || $[18] !== sections || $[19] !== unregister || $[20] !== visibilityKey) {
    t7 = () => {
      const previous = previousVisibilityKey.current;
      previousVisibilityKey.current = visibilityKey;
      if (previous === undefined) {
        return;
      }
      const gateFields = new Set(conditionFieldNames.filter(_temp2));
      const leftFieldNames = sections.filter((__0, index_1) => previous[index_1] === "1" && visibilityKey[index_1] !== "1").flatMap(_temp3).filter(name_0 => !gateFields.has(name_0));
      if (leftFieldNames.length > 0) {
        unregister(leftFieldNames);
      }
    };
    t8 = [sections, visibilityKey, conditionFieldNames, unregister];
    $[17] = conditionFieldNames;
    $[18] = sections;
    $[19] = unregister;
    $[20] = visibilityKey;
    $[21] = t7;
    $[22] = t8;
  } else {
    t7 = $[21];
    t8 = $[22];
  }
  useEffect(t7, t8);
  let t9;
  if ($[23] !== formId || $[24] !== persistStep) {
    t9 = () => {
      if (persistStep && typeof globalThis !== "undefined" && typeof formId === "string" && formId !== "") {
        const savedStep = sessionStorage.getItem(getFormStorageKey(formId, "step"));
        if (savedStep !== null) {
          return Number(savedStep);
        }
      }
      return 0;
    };
    $[23] = formId;
    $[24] = persistStep;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const [currentStepIndex, setCurrentStepIndex] = useState(t9);
  const clampedStepIndex = steps.length === 0 ? 0 : Math.min(Math.max(currentStepIndex, 0), steps.length - 1);
  let t10;
  let t11;
  if ($[26] !== clampedStepIndex || $[27] !== formId || $[28] !== persistStep) {
    t10 = () => {
      if (persistStep && typeof formId === "string" && formId !== "") {
        sessionStorage.setItem(getFormStorageKey(formId, "step"), String(clampedStepIndex));
      }
    };
    t11 = [formId, clampedStepIndex, persistStep];
    $[26] = clampedStepIndex;
    $[27] = formId;
    $[28] = persistStep;
    $[29] = t10;
    $[30] = t11;
  } else {
    t10 = $[29];
    t11 = $[30];
  }
  useEffect(t10, t11);
  const currentActualStep = steps[clampedStepIndex];
  const isFirstStep = clampedStepIndex === 0;
  const isLastStep = clampedStepIndex === steps.length - 1;
  let t12;
  if ($[31] !== currentActualStep || $[32] !== formMethods) {
    t12 = () => {
      const fieldNames = [];
      if (!currentActualStep || !("fields" in currentActualStep)) {
        return [];
      }
      function processFields(fieldsToProcess) {
        for (const field_0 of fieldsToProcess) {
          if (field_0.blockType === "conditionedBlock") {
            const {
              field: conditionField,
              value: targetValue
            } = field_0.displayCondition;
            const watchValue = formMethods.watch(conditionField) as string | boolean | number | undefined;
            const condition = (watchValue ?? "").toString() === targetValue;
            if (condition) {
              processFields(field_0.fields);
            }
          } else {
            fieldNames.push(...getFieldNames(field_0));
          }
        }
      }
      processFields(currentActualStep.fields);
      return fieldNames;
    };
    $[31] = currentActualStep;
    $[32] = formMethods;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const getVisibleFields = t12;
  let t13;
  if ($[34] !== clampedStepIndex || $[35] !== formId || $[36] !== formMethods || $[37] !== getVisibleFields || $[38] !== isLastStep) {
    t13 = async event => {
      event?.preventDefault();
      const fields = getVisibleFields();
      const isValid = await formMethods.trigger(fields, {
        shouldFocus: true
      });
      if (isValid && !isLastStep) {
        setCurrentStepIndex(clampedStepIndex + 1);
        scrollFormTopIntoView(formId);
      }
      return isValid;
    };
    $[34] = clampedStepIndex;
    $[35] = formId;
    $[36] = formMethods;
    $[37] = getVisibleFields;
    $[38] = isLastStep;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const next = t13;
  let t14;
  if ($[40] !== clampedStepIndex || $[41] !== formId || $[42] !== isFirstStep) {
    t14 = event_0 => {
      event_0?.preventDefault();
      if (!isFirstStep) {
        setCurrentStepIndex(clampedStepIndex - 1);
      }
      scrollFormTopIntoView(formId);
    };
    $[40] = clampedStepIndex;
    $[41] = formId;
    $[42] = isFirstStep;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const previous_0 = t14;
  let t15;
  if ($[44] !== clampedStepIndex || $[45] !== currentActualStep || $[46] !== isFirstStep || $[47] !== isLastStep || $[48] !== next || $[49] !== previous_0 || $[50] !== steps) {
    t15 = {
      currentStepIndex: clampedStepIndex,
      setCurrentStepIndex,
      currentActualStep,
      steps,
      isFirstStep,
      isLastStep,
      next,
      prev: previous_0
    };
    $[44] = clampedStepIndex;
    $[45] = currentActualStep;
    $[46] = isFirstStep;
    $[47] = isLastStep;
    $[48] = next;
    $[49] = previous_0;
    $[50] = steps;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  return t15;
};
function _temp(section) {
  const field = section.displayCondition?.field;
  return typeof field === "string" && field !== "" ? field : "";
}
function _temp2(name) {
  return name !== "";
}
function _temp3(section_1) {
  return collectFieldNames(section_1.fields);
}